import { get } from 'lodash';
import queryString from 'query-string';
import * as moment from 'moment-timezone';
import { createFetchAction } from '../helpers/fetch';
import { getToken, logout, buildApiUrl, buildTenantApiUri } from '../../helpers';
import { CUSTOM_TOASTER } from '../toaster/toaster';
import { simpleSignError } from '../helpers/simpleSignError';

// Actions
export const CREATE_SIGNATURE_REQUEST = 'CREATE_SIGNATURE_REQUEST';
export const GET_SIGN_URL = 'GET_SIGN_URL';
export const GET_ONBOARDING_DOCS = 'GET_ONBOARDING_DOCS';
export const UPDATE_SIGNATURE_STATUS = 'UPDATE_SIGNATURE_STATUS';
export const CREATE_ONBOARDING_SIMPLE_SIGN_DOCUMENT = 'CREATE_ONBOARDING_SIMPLE_SIGN_DOCUMENT';

// Action Creators
export function createSignatureRequest(templateId, toaster, sendToAll = false, employees = [], isSimpleSign = true) {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  return createFetchAction(CREATE_SIGNATURE_REQUEST, {
    url: `${buildApiUrl(token, companyId)}/requests`,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      templateId,
      subject: 'Subject',
      message: 'Message',
      signatories: sendToAll ? [
        {
          employeeCode: 'all',
          role: 'OnboardingSignatory',
        },
      ] : employees.map(employee =>
        ({
          role: 'OnboardingSignatory',
          employeeCode: employee.value,
        })),
      isSimpleSign,
    }),
  }, 'CREATING_SIGNATURE_REQUEST', {
    fallbackAction: logout,
    errorCodes: [401],
    failureMessage: (status, response) => {
      if (status && status === 422 && response && response.moreInfo) {
        const { successes, failures } = JSON.parse(response.moreInfo);
        return {
          type: CUSTOM_TOASTER,
          customComponent: toaster(successes, failures),
          severity: 'warning',
          timeout: null,
        };
      }
      return undefined;
    },
  });
}

export function getSignUrl(signatureId) {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const employeeId = get(window, 'asure.appSettings.selectedEmployee.employeeId');
  return createFetchAction(GET_SIGN_URL, {
    url: `${buildApiUrl(token, companyId, employeeId)}/${signatureId}/sign-url`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }, 'GETTING_SIGN_URL', {
    fallbackAction: logout,
    errorCodes: [401],
    signatureId,
  });
}

// The onboarding endpoint returns a list of signature requests generated
// from a list of templates specified in the onboarding task list
export function getOnboardingDocs() {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const emailAddress = get(window, 'asure.appSettings.selectedEmployee.emailAddress');
  const employeeName = get(window, 'asure.appSettings.selectedEmployee.name');
  const taskListId = Number(get(window, 'asure.appSettings.esignature.taskList.id'));
  const onboardingKey = get(window, 'asure.appSettings.esignature.onboarding.key') || queryString.parse(window.location.search).onboardingkey;
  const employeeCode = get(window, 'asure.appSettings.selectedEmployee.employeeCode');
  return createFetchAction(GET_ONBOARDING_DOCS, {
    url: `${buildApiUrl(token, companyId)}/requests/onboarding`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      onboardingKey,
      taskListId,
      emailAddress,
      name: employeeName,
      employeeCode,
    }),
  }, 'GETTING_ONBOARDING_DOCS', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}
export function createOnboardingSimpleSignDocument(documentId) {
  const token = getToken();
  const onboardingKey = get(window, 'asure.appSettings.esignature.onboarding.key') || queryString.parse(window.location.search).onboardingkey;
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  return createFetchAction(CREATE_ONBOARDING_SIMPLE_SIGN_DOCUMENT, {
    url: `${buildTenantApiUri(token)}/companies/${companyId}/onboarding/${onboardingKey}/documents`,
    method: 'POST',
    body: JSON.stringify({
      signatureRequestId: documentId,
      timeZone: moment.tz.guess(),
    }),
  }, 'CREATING_SIMPLE_SIGN_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
    failureMessage: simpleSignError,
    documentId,
  });
}
export function updateSignatureStatus(signatureId, status) {
  return {
    type: UPDATE_SIGNATURE_STATUS,
    payload: {
      signatureId,
      status,
    },
  };
}

const INITIAL_STATE = {
  id: '',
  title: '',
  status: '',
  signatures: [],
  signUrl: {},
  onboardingDocs: [],
  sentSignatureRequests: [],
};

export function signatureRequest(state = INITIAL_STATE, action) {
  switch (action.type) {
    case CREATE_SIGNATURE_REQUEST: {
      const { payload } = action;
      return {
        ...state,
        sentSignatureRequests: payload,
      };
    }
    case GET_SIGN_URL: {
      const { payload, meta: { signatureId } } = action;
      payload.signatureId = signatureId;
      return {
        ...state,
        signUrl: payload,
      };
    }
    case GET_ONBOARDING_DOCS: {
      const { payload } = action;
      const onboardingDocs = payload && payload.results ? payload.results : [];
      return {
        ...state,
        onboardingDocs,
      };
    }
    case CREATE_ONBOARDING_SIMPLE_SIGN_DOCUMENT: {
      const { payload, meta: { documentId } } = action;
      if (!payload) {
        return state;
      }
      const { onboardingDocs } = state;
      const documents = onboardingDocs.map(document => (document.id === documentId ? { ...document, status: payload.status.name } : document));
      return {
        ...state,
        onboardingDocs: documents,
      };
    }
    case UPDATE_SIGNATURE_STATUS: {
      const { payload: { signatureId, status } } = action;
      const { onboardingDocs } = state;
      const updatedDocs = [...onboardingDocs];
      const updatedDoc = updatedDocs.filter(doc => doc.id === signatureId)[0];
      if (updatedDoc) {
        updatedDoc.status = status;
      }
      return {
        ...state,
        onboardingDocs: [...updatedDocs],
      };
    }
    default:
      break;
  }
  return state;
}



// WEBPACK FOOTER //
// ./src/modules/signatureRequest/signatureRequest.js